import type { EvenementJournalVue, FiltresJournalVue, PageJournalVue, ScreenFilterDef, ScreenStat } from '@taki/ui';

/**
 * L'ECRAN DU JOURNAL, SANS REACT : les mots, la periode, l'export CSV.
 *
 * Tout ce qui se calcule sans navigateur vit ici, pour se tester sans DOM
 * (`packages/shell/tests/journal.test.mjs`). L'ecran est `screens/JournalScreen.tsx`,
 * le format des evenements `docs/JOURNAL.md`.
 */

/** Les categories, dans l'ordre du menu de l'ecran. `''` : toutes. */
export const CATEGORIES_JOURNAL: readonly { id: string; label: string }[] = [
  { id: '', label: 'Tout' },
  { id: 'auth', label: 'Connexions' },
  { id: 'settings', label: 'Réglages' },
  { id: 'access', label: 'Accès' },
  { id: 'ia', label: 'Lia et MCP' },
  { id: 'app', label: 'Apps' },
];

/** Les resultats, dans l'ordre du catalogue de filtres. */
export const RESULTATS_JOURNAL: readonly { id: string; label: string }[] = [
  { id: 'ok', label: 'Réussi' },
  { id: 'denied', label: 'Refusé' },
  { id: 'error', label: 'Erreur' },
];

/** Les periodes proposees, en jours (filtres du catalogue). */
export const PERIODES_JOURNAL: readonly { id: string; label: string; jours: number }[] = [
  { id: '1', label: '24 dernières heures', jours: 1 },
  { id: '7', label: '7 derniers jours', jours: 7 },
  { id: '30', label: '30 derniers jours', jours: 30 },
  { id: '365', label: '12 derniers mois', jours: 365 },
];

/** Nombre de lignes au plus dans un export : au-dela, on resserre le filtre. */
export const PLAFOND_EXPORT = 5000;

/**
 * Nombre d'evenements au plus qu'une porte charge (12 derniers mois de sa
 * categorie). Le moteur d'ecran filtre, cherche et trie en memoire : au-dela,
 * les plus anciens ne sont pas lus, et la stat le dit (`2000+`).
 */
export const PLAFOND_CHARGE = 2000;

/** Le debut d'une periode, depuis `maintenant`. */
export function debutDePeriode(jours: number, maintenant: Date = new Date()): Date {
  return new Date(maintenant.getTime() - jours * 24 * 60 * 60 * 1000);
}

const ACTIONS: Readonly<Record<string, string>> = {
  'auth.signin': 'Connexion',
  'auth.claims.set': 'Droits posés sur le compte',
  'settings.ia.update': 'Réglage IA modifié',
  'settings.branding.update': 'Apparence modifiée',
  'settings.branding.reset': 'Apparence remise à zéro',
  'settings.apps.install': 'App installée',
  'settings.apps.uninstall': 'App désinstallée',
  'settings.environnement.update': 'Environnement modifié',
  'settings.app.update': 'App personnalisée',
  'settings.app.options': 'Options de l’app',
  'settings.app.reset': 'App revenue au modèle',
  'settings.market-key.set': 'Clé de marché posée',
  'settings.market-key.remove': 'Clé de marché retirée',
  'access.member.create': 'Membre invité',
  'access.member.update': 'Membre modifié',
  'access.member.archive': 'Membre retiré',
  'access.member.restore': 'Membre rétabli',
  'access.account.create': 'Compte créé',
  'access.account.password': 'Mot de passe renouvelé',
  'access.account.remove': 'Accès retiré',
  'access.ia-connection.create': 'Connexion MCP ouverte',
  'access.ia-connection.revoke': 'Connexion MCP révoquée',
  'access.connexion.connect': 'Connexion reliée',
  'access.connexion.update': 'Connexion réglée',
  'access.connexion.remove': 'Connexion supprimée',
  'ia.ask': 'Question à Lia',
  'ia.tool': 'Outil IA',
  'ia.studio.apply': 'Studio : champ ajouté',
};

/** Le libelle d'une action, en francais ; une action inconnue se montre telle quelle. */
export function libelleDAction(action: string): string {
  const connu = ACTIONS[action];
  if (connu) return connu;
  const [categorie, appId, verbe] = action.split('.');
  if (categorie === 'app' && appId && verbe) return `${appId} : ${verbe.replaceAll('-', ' ')}`;
  return action;
}

const RESULTATS: Readonly<Record<string, string>> = { ok: 'Réussi', denied: 'Refusé', error: 'Erreur' };

export function libelleDeResultat(outcome: string): string {
  return RESULTATS[outcome] ?? outcome;
}

const SOURCES: Readonly<Record<string, string>> = {
  web: 'Écran', ask: 'Lia', lia: 'Lia', automatisation: 'Automatisation', mcp: 'Client MCP', function: 'Serveur', scheduler: 'Tâche planifiée', script: 'Script',
};

export function libelleDeSource(source: string): string {
  return SOURCES[source] ?? source;
}

function valeur(v: string | number | boolean | null | undefined): string {
  if (v === undefined) return '';
  if (v === null) return 'vide';
  if (typeof v === 'boolean') return v ? 'oui' : 'non';
  return String(v);
}

/** Le detail d'une ligne : les champs changes, puis les precisions utiles. */
export function detailDEvenement(ev: EvenementJournalVue): string {
  const morceaux: string[] = [];
  for (const ligne of ev.changes ?? []) {
    morceaux.push(ligne.before !== undefined || ligne.after !== undefined
      ? `${ligne.field} : ${valeur(ligne.before) || '-'} -> ${valeur(ligne.after) || '-'}`
      : ligne.field);
  }
  const meta = ev.meta ?? {};
  // Une reponse de Lia a une mention dit d'ou venait la question (« mention dans #general par Celia »).
  if (typeof meta['mention'] === 'string' && meta['mention'] !== '') morceaux.push(String(meta['mention']));
  if (typeof meta['tool'] === 'string') morceaux.push(`outil ${meta['tool']}`);
  if (typeof meta['tools'] === 'string' && meta['tools'] !== '') morceaux.push(`outils ${String(meta['tools']).replaceAll(',', ', ')}`);
  if (typeof meta['provider'] === 'string') morceaux.push(`via ${meta['provider']}`);
  if (typeof meta['durationMs'] === 'number') morceaux.push(`${(Number(meta['durationMs']) / 1000).toFixed(1)} s`);
  if (typeof meta['migrated'] === 'number') morceaux.push(`${meta['migrated']} fiche(s) modifiée(s)`);
  if (typeof meta['count'] === 'number') morceaux.push(`${meta['count']} fiche(s) concernée(s)`);
  if (typeof meta['reason'] === 'string') morceaux.push(`motif ${meta['reason']}`);
  if (typeof meta['code'] === 'string') morceaux.push(`code ${meta['code']}`);
  return morceaux.join(' · ');
}

/** La cible, lisible : `contacts / c1`, ou le libelle si l'evenement en porte un. */
export function cibleDEvenement(ev: EvenementJournalVue): string {
  if (ev.target === null) return '';
  return ev.target.label ?? `${ev.target.collection} / ${ev.target.id}`;
}

/**
 * Une cellule CSV : entre guillemets, guillemets doubles, et une formule
 * neutralisee. Un tableur EXECUTE une cellule qui commence par `=`, `+`, `-`
 * ou `@` : un libelle d'app ou une adresse piegee deviendrait une formule
 * chez le proprietaire qui ouvre l'export.
 */
export function celluleCsv(brut: string): string {
  const sur = /^[=+\-@\t\r]/.test(brut) ? `'${brut}` : brut;
  return `"${sur.replaceAll('"', '""')}"`;
}

export const COLONNES_CSV = ['date', 'categorie', 'action', 'libelle', 'acteur', 'role', 'source', 'cible', 'detail', 'resultat'] as const;

/** Le fichier CSV d'une liste d'evenements (separateur `;`, lu tel quel par un tableur francais). */
export function versCsv(events: readonly EvenementJournalVue[]): string {
  const lignes = [COLONNES_CSV.join(';')];
  for (const ev of events) {
    lignes.push([
      ev.at.toISOString(),
      ev.category,
      ev.action,
      libelleDAction(ev.action),
      ev.actor.email ?? ev.actor.uid ?? '',
      ev.actor.role ?? '',
      ev.actor.source,
      cibleDEvenement(ev),
      detailDEvenement(ev),
      ev.outcome,
    ].map(celluleCsv).join(';'));
  }
  // La marque d'ordre des octets fait lire les accents juste a Excel.
  return `\uFEFF${lignes.join('\r\n')}\r\n`;
}

/**
 * Tout le filtre courant, page apres page, jusqu'a `plafond` lignes. Rend les
 * evenements et `complet: false` quand le plafond a coupe.
 */
export async function toutLeFiltre(
  lister: (filtres: FiltresJournalVue) => Promise<PageJournalVue>,
  filtres: FiltresJournalVue,
  plafond: number = PLAFOND_EXPORT,
): Promise<{ events: EvenementJournalVue[]; complet: boolean }> {
  const events: EvenementJournalVue[] = [];
  let cursor: string | null = null;
  do {
    const page = await lister({ ...filtres, cursor, pageSize: 200 });
    events.push(...page.events);
    cursor = page.next;
  } while (cursor !== null && events.length < plafond);
  return { events: events.slice(0, plafond), complet: cursor === null && events.length <= plafond };
}

/** Le nom du fichier exporte : `journal-<org>-<date>.csv`. */
export function nomDExport(orgId: string, maintenant: Date = new Date()): string {
  return `journal-${orgId}-${maintenant.toISOString().slice(0, 10)}.csv`;
}

const JOUR_MS = 24 * 60 * 60 * 1000;

/**
 * LE CATALOGUE DE FILTRES de l'ecran, tire des evenements charges : chaque
 * personne, chaque type d'evenement et chaque app qui y figurent, les periodes,
 * et les resultats autres que « Reussi » presents. Le moteur combine les
 * filtres actifs en OU, comme sur tous les ecrans natifs.
 */
export function filtresDuJournal(
  events: readonly EvenementJournalVue[],
  contexte: {
    personnes?: readonly { userId: string | null; displayName: string }[];
    apps?: readonly { id: string; label: string }[];
    nomDe?: (ev: EvenementJournalVue) => string;
  } = {},
  maintenant: () => number = Date.now,
): ScreenFilterDef<EvenementJournalVue>[] {
  const nomDe = contexte.nomDe ?? ((ev: EvenementJournalVue) => ev.actor.email ?? ev.actor.uid ?? 'Inconnu');
  const parAuteur = new Map<string, string>();
  const parType = new Map<string, string>();
  const parApp = new Set<string>();
  const resultats = new Set<string>();
  for (const ev of events) {
    if (ev.actor.uid) parAuteur.set(ev.actor.uid, nomDe(ev));
    parType.set(ev.action, libelleDAction(ev.action));
    if (ev.target?.appId) parApp.add(ev.target.appId);
    resultats.add(ev.outcome);
  }
  const trie = (a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label, 'fr');
  const nomDApp = new Map((contexte.apps ?? []).map((a) => [a.id, a.label]));
  return [
    ...[...parAuteur].map(([uid, nom]) => ({ id: `personne:${uid}`, label: nom, match: (ev: EvenementJournalVue) => ev.actor.uid === uid })).sort(trie),
    ...[...parType].map(([action, libelle]) => ({ id: `type:${action}`, label: libelle, match: (ev: EvenementJournalVue) => ev.action === action })).sort(trie),
    ...[...parApp].map((appId) => ({ id: `app:${appId}`, label: `App ${nomDApp.get(appId) ?? appId}`, match: (ev: EvenementJournalVue) => ev.target?.appId === appId })).sort(trie),
    ...PERIODES_JOURNAL.filter((p) => p.jours < 365).map((p) => ({
      id: `periode:${p.id}`, label: p.label, match: (ev: EvenementJournalVue) => ev.at.getTime() >= maintenant() - p.jours * JOUR_MS,
    })),
    ...RESULTATS_JOURNAL.filter((r) => r.id !== 'ok' && resultats.has(r.id)).map((r) => ({
      id: `resultat:${r.id}`, label: `${r.label}s`, match: (ev: EvenementJournalVue) => ev.outcome === r.id,
    })),
  ];
}

/** Les trois stats de la porte (zone 2c), sur son jeu brut. */
export function statsDuJournal(events: readonly EvenementJournalVue[], tronque = false): ScreenStat[] {
  const n = events.length;
  const personnes = new Set(events.map((ev) => ev.actor.uid).filter(Boolean)).size;
  const refus = events.filter((ev) => ev.outcome !== 'ok').length;
  return [
    { value: `${n}${tronque ? '+' : ''}`, label: n > 1 ? 'événements' : 'événement' },
    { value: String(personnes), label: personnes > 1 ? 'personnes' : 'personne' },
    { value: String(refus), label: refus > 1 ? 'refus ou erreurs' : 'refus ou erreur' },
  ];
}
